<template>
  <el-scrollbar
    class="main-menu v-main-menu"
    :class="menuClasses"
    inverse
  >
    <nav class="menu">
      <el-menu
        ref="menu"
        :default-active="activeMenu"
        :default-openeds="[activeMenu]"
        :router="true"
        scroll-into-expanded
      >
        <el-submenu
          v-if="$can('read', 'overview')"
          key="/monitoring"
          data-testid="monitoring"
          index="/monitoring"
        >
          <template #icon>
            <el-icon name="i-overview-ab-o--32" />
          </template>

          <template #title>
            <div class="menu_with_tag">
              <span class="el-text--ellipsis">
                {{ $t('MONITORING') }}
              </span>
              <alerts-counter
                v-if="activeMenu !== '/monitoring'"
                :counters="alertsCounts"
              ></alerts-counter>
            </div>
          </template>

          <el-menu-item
            v-if="storageCluster"
            key="/monitoring/dashboard"
            data-testid="overview"
            index="/monitoring/dashboard"
          >
            {{ $t('Dashboard') }}
          </el-menu-item>

          <el-menu-item
            v-if="$can('read', 'alerts')"
            key="/monitoring/alerts"
            data-testid="alerts"
            index="/monitoring/alerts"
          >
            <div class="menu_with_tag">
              <span class="el-text--ellipsis">
                {{ $t('Alerts') }}
              </span>
              <alerts-counter
                v-if="activeMenu === '/monitoring'"
                :counters="alertsCounts"
              ></alerts-counter>
            </div>
          </el-menu-item>

          <el-menu-item
            v-if="isComputeConfigured"
            key="/monitoring/activities"
            data-testid="activities"
            index="/monitoring/activities"
          >
            {{ $t('Activities') }}
          </el-menu-item>

          <el-menu-item
            v-if="$can('read', 'audit_log')"
            key="/monitoring/audit-log"
            data-testid="audit"
            index="/monitoring/audit-log"
          >
            {{ $t('Audit log') }}
          </el-menu-item>
        </el-submenu>

        <el-menu-item
          v-if="$featureOn('vhs') && !isVhsDeployed"
          key="/vhs"
          data-testid="vhs"
          index="/vhs"
        >
          <template #icon>
            <el-icon name="i-vhs-o--32" />
          </template>
          <template #title>
            {{ $t('HYBRID SERVER') }}
          </template>
        </el-menu-item>

        <el-submenu
          v-if="$featureOn('vhs') && isVhsDeployed"
          key="/vhs"
          data-testid="vhs"
          index="/vhs"
        >
          <template #icon>
            <el-icon name="i-vhs-o--32" />
          </template>
          <template #title>
            {{ $t('HYBRID SERVER') }}
          </template>
          <el-menu-item
            key="/vhs/nodes"
            data-testid="vhs-nodes"
            index="/vhs/nodes"
          >
            {{ $t('Nodes') }}
          </el-menu-item>
          <el-menu-item
            key="/vhs/ves"
            data-testid="vhs-ves"
            index="/vhs/ves"
          >
            {{ $t('Virtual environments') }}
          </el-menu-item>
          <el-menu-item
            key="/vhs/networks"
            data-testid="vhs-networks"
            index="/vhs/networks"
          >
            {{ $t('Networks') }}
          </el-menu-item>
          <el-menu-item
            key="/vhs/datastores"
            data-testid="vhs-datastores"
            index="/vhs/datastores"
          >
            {{ $t('Datastores') }}
          </el-menu-item>
          <el-menu-item
            key="/vhs/tasks"
            data-testid="vhs-tasks"
            index="/vhs/tasks"
          >
            {{ $t('Tasks') }}
          </el-menu-item>
        </el-submenu>

        <!--TODO Add v-if with user.permissions compute-->
        <el-submenu
          v-if="isComputeConfigured"
          key="/compute"
          data-testid="compute"
          index="/compute"
        >
          <template #icon>
            <el-icon name="i-virtual-machines-o--32" />
          </template>
          <template #title>
            {{ $t('COMPUTE') }}
          </template>

          <el-menu-item
            v-if="$can('read', 'compute_cluster_stat')"
            key="/compute/overview"
            data-testid="compute-overview"
            index="/compute/overview"
          >
            {{ $t('Overview') }}
          </el-menu-item>

          <el-menu-item
            v-if="$can('read', 'compute_nodes')"
            key="/compute/nodes"
            :class="{'is-active': activeSubmenuItem === 'nodes'}"
            data-testid="compute-nodes"
            index="/compute/nodes"
          >
            {{ $t('Nodes') }}
          </el-menu-item>

          <el-menu-item
            v-if="$can('read', 'compute_servers')"
            key="/compute/servers"
            :class="{'is-active': activeSubmenuItem === 'servers'}"
            data-testid="compute-servers"
            index="/compute/servers"
          >
            {{ $t('Virtual machines') }}
          </el-menu-item>

          <el-menu-item
            v-if="$can('read', 'compute_networks')"
            key="/compute/network"
            :class="{'is-active': activeSubmenuItem === 'network'}"
            data-testid="compute-networks"
            index="/compute/network"
          >
            {{ $t('Network') }}
          </el-menu-item>

          <el-menu-item
            v-if="$can('read', 'compute_volumes')"
            key="/compute/storage"
            :class="{'is-active': activeSubmenuItem === 'storage'}"
            data-testid="compute-volumes"
            index="/compute/storage"
          >
            {{ $t('Storage') }}
          </el-menu-item>

          <el-menu-item
            key="/compute/backup"
            :class="{'is-active': activeSubmenuItem === 'backup'}"
            data-testid="compute-backup"
            index="/compute/backup"
          >
            {{ $t('Backup') }}
          </el-menu-item>

          <el-menu-item
            v-if="$can('read', 'compute_infra_optim_policies') && drsEnabled"
            key="/compute/drs"
            :class="{'is-active': activeSubmenuItem === 'drs'}"
            data-testid="compute-drs"
            index="/compute/drs"
          >
            {{ $t('Resource policies') }}
          </el-menu-item>

          <el-menu-item
            v-if="$can('read', 'compute_k8saas_clusters') && computeFeatures.k8saas"
            key="/compute/kubernetes/clusters"
            :class="{'is-active': activeSubmenuItem === 'kubernetes'}"
            data-testid="compute-k8s-clusters"
            index="/compute/kubernetes/clusters"
          >
            {{ $t('Kubernetes') }}
          </el-menu-item>

          <el-menu-item
            v-if="$can('read', 'compute_stacks') && $featureOn('stacks')"
            key="/compute/stacks"
            :class="{'is-active': activeSubmenuItem === 'stacks'}"
            data-testid="compute-stacks"
            index="/compute/stacks"
          >
            {{ $t('Stacks') }}
          </el-menu-item>

          <el-menu-item
            v-if="computeFeatures.maas"
            key="/compute/migration"
            :class="{'is-active': activeSubmenuItem === 'migration'}"
            data-testid="compute-migration"
            index="/compute/migration"
          >
            {{ $t('Migration') }}
          </el-menu-item>

          <el-menu-item
            v-if="computeFeatures['metering']"
            key="/compute/billing-metering"
            :class="{'is-active': activeSubmenuItem === 'billing-metering'}"
            data-testid="compute-billing-metering"
            index="/compute/billing-metering"
          >
            {{ $t('Billing metering') }}
          </el-menu-item>

          <el-menu-item
            v-if="$can('update', 'compute_cluster')"
            key="/compute/settings"
            :class="{'is-active': activeSubmenuItem === 'settings'}"
            data-testid="compute-settings"
            index="/compute/settings"
          >
            {{ $t('Settings') }}
          </el-menu-item>
        </el-submenu>

        <el-menu-item
          v-else-if="storageCluster"
          key="/compute"
          data-testid="install-compute"
          index="/compute"
        >
          <template #icon>
            <el-icon name="i-virtual-machines-o--32" />
          </template>
          <template #title>
            {{ $t('COMPUTE') }}
          </template>
        </el-menu-item>

        <!--TODO Add v-if with user.permissions storage-->
        <el-submenu
          v-if="storageCluster"
          key="/storage"
          data-testid="storage-services"
          index="/storage"
        >
          <template #icon>
            <el-icon name="i-services-o--32" />
          </template>
          <template #title>
            {{ $t('STORAGE SERVICES') }}
          </template>

          <el-menu-item
            v-if="$can('read', 'abgw_storage_params')"
            key="/storage/abgw"
            data-testid="abgw"
            index="/storage/abgw"
          >
            {{ $t('Backup storage') }}
          </el-menu-item>

          <el-menu-item
            key="/storage/block"
            data-testid="block-storage"
            index="/storage/block"
          >
            {{ $t('Block storage') }}
          </el-menu-item>

          <el-menu-item
            key="/storage/s3-storage"
            data-testid="s3"
            index="/storage/s3-storage"
          >
            {{ $t('S3') }}
          </el-menu-item>

          <el-menu-item
            key="/storage/nfs-storage"
            data-testid="nfs"
            index="/storage/nfs-storage"
          >
            {{ $t('NFS') }}
          </el-menu-item>

          <el-menu-item
            key="/storage/file-storage"
            data-testid="file-storage"
            index="/storage/file-storage"
          >
            {{ $t('File storage') }}
          </el-menu-item>
        </el-submenu>

        <!--TODO Add v-if with user.permissions infrastructure-->
        <el-submenu
          key="/infrastructure"
          data-testid="infrastructure"
          index="/infrastructure"
        >
          <template #icon>
            <el-icon name="i-infrastructure-o--32" />
          </template>
          <template #title>
            {{ $t('INFRASTRUCTURE') }}
          </template>

          <el-menu-item
            v-if="$can('read', 'nodes')"
            key="/infrastructure/nodes"
            data-testid="infrastructure-nodes"
            index="/infrastructure/nodes"
          >
            {{ $t('Nodes') }}
          </el-menu-item>

          <el-menu-item
            key="/infrastructure/networks"
            data-testid="infrastructure-networks"
            index="/infrastructure/networks"
          >
            {{ $t('Networks') }}
          </el-menu-item>

          <el-menu-item
            key="/infrastructure/tiers"
            data-testid="infrastructure-tiers"
            index="/infrastructure/tiers"
          >
            {{ $t('Tiers') }}
          </el-menu-item>
        </el-submenu>

        <el-submenu
          key="/settings"
          data-testid="settings"
          index="/settings"
        >
          <template #icon>
            <el-icon name="i-cog-o--32" />
          </template>
          <template #title>
            {{ $t('SETTINGS') }}
          </template>

          <el-menu-item
            v-if="$can('read', 'license') && storageCluster"
            key="/settings/license"
            :class="{'is-active': activeSubmenuItem === '/settings/license'}"
            data-testid="settings-license"
            index="/settings/license"
          >
            {{ $t('License') }}
          </el-menu-item>

          <el-menu-item
            v-if="showClusterRegistrationMenu"
            key="/settings/cluster-registration"
            :class="{'is-active': activeSubmenuItem === '/settings/cluster-registration'}"
            data-testid="settings-cluster-registration"
            index="/settings/cluster-registration"
          >
            {{ $t('Cloud registration') }}
          </el-menu-item>

          <el-menu-item
            v-if="$can('read', 'domains')"
            key="/settings/domains"
            :class="{'is-active': activeSubmenuItem === '/settings/domains'}"
            data-testid="settings-domains"
            index="/settings/domains"
          >
            {{ $t('Projects and users') }}
          </el-menu-item>

          <el-menu-item
            key="/settings/system"
            :class="{'is-active': activeSubmenuItem === '/settings/system'}"
            data-testid="settings-system"
            index="/settings/system"
          >
            {{ $t('System settings') }}
          </el-menu-item>

          <el-menu-item
            key="/settings/updates"
            :class="{'is-active': activeSubmenuItem === '/settings/updates'}"
            data-testid="settings-updates"
            index="/settings/updates"
          >
            {{ $t('Updates') }}
          </el-menu-item>

          <el-menu-item
            v-if="isComputeConfigured"
            key="/settings/api-access"
            :class="{'is-active': activeSubmenuItem === '/settings/api-access'}"
            data-testid="settings-api-access"
            index="/settings/api-access"
          >
            {{ $t('API access') }}
          </el-menu-item>
        </el-submenu>
      </el-menu>
    </nav>
  </el-scrollbar>
</template>

<script>
  import { mapGetters } from 'vuex';
  import AlertsCounter from '@/common/shared/AlertsCounter.vue';
  import { mapState } from 'pinia';
  import useStorageClusterStore from '@/common/store/pinia/storageCluster';
  import useVhsDeployStore from '@/common/store/pinia/vhsDeploy';
  import useThemeStore from '@/common/store/pinia/theme';

  export default {
    name: 'MainMenu',
    components: { AlertsCounter },
    created() {
      this.$store.$takeFirst('alerts/FETCH_ITEMS');
    },
    computed: {
      ...mapState(useStorageClusterStore, {
        storageCluster: 'current'
      }),
      ...mapGetters({
        computeFeatures: 'compute/computeClusterFeatures',
        isComputeConfigured: 'compute/isComputeConfigured',
        computeClusterTask: 'compute/computeClusterTask',
        alertsCounts: 'alerts/counters'
      }),
      ...mapState(useVhsDeployStore, {
        isVhsDeployed: 'isDeployed',
      }),
      ...mapState(useThemeStore, {
        themeData: 'themeData'
      }),
      computeInstallingFeatures() {
        return this.computeClusterTask?.payload?.enable_features || [];
      },
      isDrsInstalling() {
        return this.computeInstallingFeatures.includes('infra-optim');
      },
      drsEnabled() {
        return this.computeFeatures['infra-optim'] && !this.isDrsInstalling;
      },
      menuClasses() {
        return {
          menuWithScroll: true,
          menuWithClusterStatus: !!this.storageCluster,
        };
      },
      showClusterRegistrationMenu() {
        return !!this.themeData.showClusterRegistrationMenu;
      },
      activeMenu() {
        const activeMatched = this.$route.matched[0];
        return activeMatched?.path;
      },
      activeSubmenuItem() {
        const matched = this.$route.matched[1];
        return matched?.meta?.href || matched?.path;
      },
    }
  };
</script>

<style lang="scss" scoped>
  .menuWithScroll {
    height: 100%;
  }

  .menuWithClusterStatus {
    height: calc(100% - 128px);
  }

  .el-menu {
    border-right: 0;

    ::v-deep .el-submenu__title {
      height: 64px;
    }
  }

  .el-menu-item.is-active {
    background: $av-nav-accent;
    color: $av-inversed-primary;
  }

  .menu_with_tag {
    display: inline-flex;
    width: 176px;

    .tags-container {
      margin-left: auto;
    }
  }
</style>
